<template>
  <div>
    <template v-for="scan in scans" :key="scan.testIdPrefix">
      <div class="subtitle1 mt-4" :data-testid="`device-scan-section-${scan.testIdPrefix}-title`">
        {{ $t(scan.titleKey) }}
      </div>
      <div
        class="body2 text--grey"
        :data-testid="`device-scan-section-${scan.testIdPrefix}-last-scan`"
      >
        {{ $t("deviceDetails.device.deviceScan.lastScan") }}
        {{ getFormattedDateTime(scan.data.lastScanDate, "ll") }}
      </div>
      <div
        class="body2 text--grey"
        :data-testid="`device-scan-section-${scan.testIdPrefix}-file-count`"
      >
        {{ $t("deviceDetails.device.deviceScan.fileCount") }}
        {{ scan.data.fileCount }}
      </div>
      <div
        class="d-flex align-center"
        :data-testid="`device-scan-section-${scan.testIdPrefix}-status`"
      >
        <span
          class="body2"
          :class="{ 'text-red-dark': scan.data.status === DeviceScanStatus.FAILED }"
        >
          {{ $t("deviceDetails.device.deviceScan.status") }}
          {{ scan.statusText }}
        </span>
        <v-progress-circular
          v-if="scan.data.status === DeviceScanStatus.SCANNING"
          class="ml-2"
          indeterminate
          size="14"
          width="2"
          :data-testid="`device-scan-section-${scan.testIdPrefix}-progress`"
        />
      </div>
      <template v-if="scan.data.status === DeviceScanStatus.COMPLETED">
        <div
          class="body2"
          :class="{ 'text-red-dark': scan.data.threatsFound > 0 }"
          :data-testid="`device-scan-section-${scan.testIdPrefix}-result`"
        >
          {{ $t("deviceDetails.device.deviceScan.result") }}
          {{
            scan.data.threatsFound > 0
              ? $t("deviceDetails.device.deviceScan.threatsFound", {
                  count: scan.data.threatsFound,
                })
              : $t("deviceDetails.device.deviceScan.noThreats")
          }}
        </div>
        <a
          v-if="scan.data.threatsFound > 0"
          class="coro-link body2"
          :data-testid="`device-scan-section-${scan.testIdPrefix}-view-log`"
          @click="$emit('scroll-to-activity-logs')"
        >
          {{ $t("deviceDetails.device.deviceScan.viewScanLog") }} ›
        </a>
      </template>
    </template>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { DeviceDetails, DeviceScanMetaData } from "@/_store/devices.module.ts";
import { DeviceScanStatus } from "@/constants/devices.ts";
import { getFormattedDateTime } from "@/_helpers/date";

export default defineComponent({
  emits: ["scroll-to-activity-logs"],
  props: {
    deviceDetails: {
      type: Object as PropType<DeviceDetails>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const getScanStatusText = (scanData: DeviceScanMetaData | undefined) => {
      if (!scanData) return "";
      const { status, progressPercent } = scanData;
      if (status === DeviceScanStatus.SCANNING) {
        return t("deviceScanStatuses.scanning", { progress: progressPercent });
      }
      return t(`deviceScanStatuses.${status}`);
    };

    const scans = computed(() =>
      [
        {
          data: props.deviceDetails.malwareScanMetaData,
          testIdPrefix: "malware",
          titleKey: "deviceDetails.device.deviceScan.malwareScanTitle",
        },
        {
          data: props.deviceDetails.dlpScanMetaData,
          testIdPrefix: "dlp",
          titleKey: "deviceDetails.device.deviceScan.dlpScanTitle",
        },
      ]
        .filter((scan) => scan.data)
        .map((scan) => ({
          ...scan,
          data: scan.data!,
          statusText: getScanStatusText(scan.data),
        }))
    );

    return {
      DeviceScanStatus,
      getFormattedDateTime,
      scans,
    };
  },
});
</script>
